import React, { useState, lazy, useContext, useEffect } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { Routes as AppRoutes } from "../routes";
import { AuthContext } from "../context/AuthContext";

// components
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Preloader from "../components/Preloader";

import { Suspense } from "react";
import StudentLogin from "./studentside/StudentLogin/Login";
import OurTeam from "./studentside/OurTeam";
import toast, { Toaster } from "react-hot-toast";

//Student Side pages
const CompanyDetails = lazy(() => import("./studentside/CompanyDetails"));
const CompanyNoticeDetails = lazy(() =>
  import("./studentside/CompanyNoticeDetails")
);
const AllCompanies = lazy(() => import("./studentside/AllCompanies"));
const CompanyNotifications = lazy(() =>
  import("./studentside/CompanyNotifications.tsx")
);
const Profile = lazy(() => import("./profile/"));
const Application = lazy(() => import("./studentside/Application"));
const Login = lazy(() => import("./studentside/StudentLogin/Login"));
const IMSLogin = lazy(() => import("./studentside/StudentLogin/ims-login"));
const EmailVerify = lazy(() =>
  import("./studentside/StudentLogin/email-verify")
);
// const Register = lazy(() => import("./studentside/StudentLogin/Register"));
const Contact = lazy(() => import("./studentside/Support/Contact"));
const TnPGuidelines = lazy(() => import("./studentside/Support/TnPGuidelines"));
const ResetPassword = lazy(() => import("./ResetPassword"));
const ForgotPassword = lazy(() => import("./ForgotPassword"));
const FeedbackForm = lazy(() =>
  import("./studentside/Support/feedback/Feedback")
);
const MyFeedbacks = lazy(() =>
  import("./studentside/Support/feedback/MyFeedbacks")
);

const LazyLoadedComponent = ({ children }) => {
  return <Suspense fallback={<div>Loading...</div>}>{children}</Suspense>;
};

const RouteWithSidebar = ({ component: Component, ...rest }) => {
  const {
    state: { isAuthenticated, profileData },
  } = useContext(AuthContext);

  const [loaded] = useState(false);

  const localStorageIsSettingsVisible = () => {
    return localStorage.getItem("settingsVisible") === "false" ? false : true;
  };

  const [showSettings, setShowSettings] = useState(
    localStorageIsSettingsVisible
  );

  const toggleSettings = () => {
    setShowSettings(!showSettings);
    localStorage.setItem("settingsVisible", !showSettings);
  };

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!profileData?.isCgpaUpdated && !sessionStorage.getItem("showUpdateCGPA")) {
    toast.error("Please update your CGPA to apply for companies!");
    sessionStorage.setItem("showUpdateCGPA", true);
  }

  return (
    <>
      <Preloader show={loaded} />
      <Sidebar />

      <main className="content">
        <Navbar />
        <LazyLoadedComponent>
          <Component {...rest} />
        </LazyLoadedComponent>
        <Footer
          toggleSettings={toggleSettings}
          showSettings={showSettings}
        />
      </main>
    </>
  );
};

// Wrapper component to handle the sidebar layout
const ProtectedRoute = ({ children }) => {
  const {
    state: { isAuthenticated, profileData },
  } = useContext(AuthContext);

  const [loaded] = useState(false);

  const localStorageIsSettingsVisible = () => {
    return localStorage.getItem("settingsVisible") === "false" ? false : true;
  };

  const [showSettings, setShowSettings] = useState(
    localStorageIsSettingsVisible
  );

  const toggleSettings = () => {
    setShowSettings(!showSettings);
    localStorage.setItem("settingsVisible", !showSettings);
  };

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!profileData?.isCgpaUpdated && !sessionStorage.getItem("showUpdateCGPA")) {
    toast.error("Please update your CGPA to apply for companies!");
    sessionStorage.setItem("showUpdateCGPA", true);
  }

  return (
    <>
      <Preloader show={loaded} />
      <Sidebar />

      <main className="content">
        <Navbar />
        <LazyLoadedComponent>
          {children}
        </LazyLoadedComponent>
        <Footer
          toggleSettings={toggleSettings}
          showSettings={showSettings}
        />
      </main>
    </>
  );
};

export default () => {
  const {
    state: { isLoading },
  } = useContext(AuthContext);
  if (isLoading) {
    return <Preloader show={true} />;
  }

  // useEffect(() => {
  //   if ("Notification" in window) {
  //     Notification.requestPermission().then((permission) => {
  //       if (permission === "denied") {
  //         toast.error("Allow notification in site setting to stay updated!");
  //       }
  //     });
  //   }
  // }, []);

  return (
    <>
      <Toaster
        position="top-right"
        reverseOrder={false}
        toastOptions={{ duration: 5000 }}
      />
      <Routes>
        <Route
          path="/login"
          element={
            <LazyLoadedComponent>
              <Login />
            </LazyLoadedComponent>
          }
        />
        <Route
          path="/register"
          element={
            <LazyLoadedComponent>
              <IMSLogin />
            </LazyLoadedComponent>
          }
        />
        <Route
          path="/email-verified"
          element={
            <LazyLoadedComponent>
              <EmailVerify />
            </LazyLoadedComponent>
          }
        />
        {/* <Route
          path="/register"
          element={
            <LazyLoadedComponent>
              <Register />
            </LazyLoadedComponent>
          }
        /> */}
        <Route
          path="/forgot-password"
          element={
            <LazyLoadedComponent>
              <ForgotPassword />
            </LazyLoadedComponent>
          }
        />
        <Route
          path="/reset-password"
          element={
            <LazyLoadedComponent>
              <ResetPassword />
            </LazyLoadedComponent>
          }
        />
        <Route
          path={AppRoutes.AllCompanies.path}
          element={
            <ProtectedRoute>
              <AllCompanies />
            </ProtectedRoute>
          }
        />
        <Route
          path={AppRoutes.CompanyDetails.path}
          element={
            <ProtectedRoute>
              <CompanyDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path={AppRoutes.CompanyNoticeDetails.path}
          element={
            <ProtectedRoute>
              <CompanyNoticeDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path={AppRoutes.Profile.path}
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path={AppRoutes.Application.path}
          element={
            <ProtectedRoute>
              <Application />
            </ProtectedRoute>
          }
        />
        <Route
          path={AppRoutes.CompanyNotifications.path}
          element={
            <ProtectedRoute>
              <CompanyNotifications />
            </ProtectedRoute>
          }
        />
        <Route
          path={AppRoutes.TnPGuidelines.path}
          element={
            <ProtectedRoute>
              <TnPGuidelines />
            </ProtectedRoute>
          }
        />
        <Route
          path={AppRoutes.ResetPassword.path}
          element={
            <ProtectedRoute>
              <ResetPassword />
            </ProtectedRoute>
          }
        />
        <Route
          path={AppRoutes.Contact.path}
          element={
            <ProtectedRoute>
              <Contact />
            </ProtectedRoute>
          }
        />
        <Route
          path={AppRoutes.OurTeam.path}
          element={
            <ProtectedRoute>
              <OurTeam />
            </ProtectedRoute>
          }
        />
        <Route
          path={AppRoutes.StudentLogin.path}
          element={
            <ProtectedRoute>
              <StudentLogin />
            </ProtectedRoute>
          }
        />
        <Route
          path={AppRoutes.Feedback.path}
          element={
            <ProtectedRoute>
              <FeedbackForm />
            </ProtectedRoute>
          }
        />
        <Route
          path={AppRoutes.MyFeedback.path}
          element={
            <ProtectedRoute>
              <MyFeedbacks />
            </ProtectedRoute>
          }
        />
      </Routes>
    </>
  );
};
